/**
 * Surface Layout
 *
 * Arranges surfaces within a canvas according to the layout hint:
 * - tabs: tab bar + one visible surface at a time (DnD on tab buttons)
 * - stack: vertical list of all surfaces (vertical DnD)
 * - grid: 2-column CSS grid (no DnD; reorder via three-dot menu)
 */

import { cleanDisplayText } from '@/utils/display-text';
import { useState, useCallback, useEffect, useRef, forwardRef } from 'react';
import { AnimatePresence } from 'motion/react';
import { Layers, GripVertical } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AuiSurface } from '../aui/AuiSurface';
import { AnimatedListItem } from '../ui/AnimatedListItem';
import { useCanvases } from '../../contexts/CanvasContext';
import { spaceApi } from '../../services/api';
import type { Surface, AuiAction, CanvasLayout } from '../../types/canvases';

interface SurfaceLayoutProps {
  layout: CanvasLayout;
  surfaces: Surface[];
  onAction?: (action: AuiAction, surfaceName: string) => Promise<void>;
  onReorder?: (orderedSurfaceIds: string[]) => void;
}

export function SurfaceLayout({ layout, surfaces, onAction, onReorder }: SurfaceLayoutProps) {
  const [activeSurfaceIdx, setActiveSurfaceIdx] = useState(0);
  const { dispatch, scrollToSurface, canvases, removeCanvas, spaceId } = useCanvases();

  // Track canvas ID to detect canvas switches vs. incremental surface changes.
  // When the canvas changes, skip entrance animations so surfaces appear instantly.
  const currentCanvasId = surfaces[0]?.canvasId ?? null;
  const prevCanvasIdRef = useRef(currentCanvasId);
  const skipEntrance = prevCanvasIdRef.current !== currentCanvasId;
  useEffect(() => {
    prevCanvasIdRef.current = currentCanvasId;
  });

  // Clamp active index when surfaces change (e.g. after dismiss)
  useEffect(() => {
    if (activeSurfaceIdx >= surfaces.length && surfaces.length > 0) {
      setActiveSurfaceIdx(surfaces.length - 1);
    }
  }, [activeSurfaceIdx, surfaces.length]);

  // When a scroll target lands on one of our surfaces, switch to its tab so
  // CanvasPanel's scrollIntoView can find the element. Harmless in
  // stack/grid layouts where every surface is already mounted.
  useEffect(() => {
    if (!scrollToSurface) return;
    const idx = surfaces.findIndex((s) => s.surfaceId === scrollToSurface.surfaceId);
    if (idx !== -1 && idx !== activeSurfaceIdx) {
      setActiveSurfaceIdx(idx);
    }
  }, [scrollToSurface, surfaces, activeSurfaceIdx]);

  const handleDismiss = useCallback(
    (canvasId: string, surfaceId: string) => {
      // If this is the last surface in a non-default canvas, remove the
      // canvas too — empty non-default canvases are clutter. Default
      // canvases are preserved as a landing surface even when empty.
      const cv = canvases.get(canvasId);
      const isLastSurface = surfaces.length === 1 && surfaces[0]?.surfaceId === surfaceId;
      if (isLastSurface && cv && !cv.isDefault) {
        // removeCanvas dispatches canvas_removed (clears state + switches
        // active tab) and DELETEs the canvas on the backend, which cascades
        // to its surfaces — no separate deleteSurface call needed.
        removeCanvas(canvasId);
        return;
      }
      dispatch({ type: 'surface_removed', canvasId, surfaceId });
      if (spaceId) {
        spaceApi(spaceId).canvases.deleteSurface(canvasId, surfaceId).catch(() => {});
      }
    },
    [dispatch, canvases, removeCanvas, surfaces],
  );

  // Surfaces have nested interactive content (buttons, inputs); use a
  // 6px activation distance so a click doesn't accidentally start a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const orderedIds = surfaces.map((s) => s.surfaceId);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = orderedIds.indexOf(String(active.id));
    const newIdx = orderedIds.indexOf(String(over.id));
    if (oldIdx === -1 || newIdx === -1) return;
    onReorder?.(arrayMove(orderedIds, oldIdx, newIdx));
  };

  if (surfaces.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center">
          <Layers className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium text-foreground">Canvas</h3>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Agent-generated UI will appear here when the agent renders surfaces.
          </p>
        </div>
      </div>
    );
  }

  if (layout === 'tabs') {
    const activeSurface = surfaces[activeSurfaceIdx] ?? surfaces[0];
    // Tabs always show one surface at a time, so we can safely give it the
    // full panel height. Surfaces with a fill anchor (see AuiSurface) will
    // claim it via `h-full`; content-sized surfaces stay at their natural size.
    return (
      <div className="flex flex-1 min-h-0 flex-col gap-3">
        {surfaces.length > 1 && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={orderedIds} strategy={horizontalListSortingStrategy}>
              <div className="flex gap-1 border-b border-border pb-2">
                {surfaces.map((srf, idx) => (
                  <SortableSurfaceTabButton
                    key={srf.surfaceId}
                    surface={srf}
                    isActive={idx === activeSurfaceIdx}
                    onClick={() => setActiveSurfaceIdx(idx)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
        <AnimatePresence initial={false} mode="wait">
          <AnimatedListItem key={activeSurface.surfaceId} skipEntrance={skipEntrance} fill>
            <AuiSurface
              surface={activeSurface}
              onAction={onAction ? (a) => onAction(a, activeSurface.surfaceName ?? '') : undefined}
              onDismiss={() => handleDismiss(activeSurface.canvasId, activeSurface.surfaceId)}
            />
          </AnimatedListItem>
        </AnimatePresence>
      </div>
    );
  }

  if (layout === 'grid') {
    return (
      <div className="grid grid-cols-2 gap-4">
        <AnimatePresence initial={false} mode="popLayout">
          {surfaces.map((srf) => (
            <AnimatedListItem key={srf.surfaceId} skipEntrance={skipEntrance}>
              <AuiSurface
                surface={srf}
                onAction={onAction ? (a) => onAction(a, srf.surfaceName ?? '') : undefined}
                onDismiss={() => handleDismiss(srf.canvasId, srf.surfaceId)}
              />
            </AnimatedListItem>
          ))}
        </AnimatePresence>
      </div>
    );
  }

  // stack (default) — vertical DnD with explicit drag handle in surface header
  const showStackDragHandles = surfaces.length > 1 && !!onReorder;
  // Single-surface stacks behave like tabs: the lone surface can fill the
  // panel. Multi-surface stacks stay content-sized so each surface keeps
  // its natural height and stacks vertically as before.
  const isSingleSurface = surfaces.length === 1;
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
        <div
          className={
            isSingleSurface
              ? 'flex flex-1 min-h-0 flex-col gap-6'
              : 'flex flex-col gap-6'
          }
        >
          <AnimatePresence initial={false} mode="popLayout">
            {surfaces.map((srf) => (
              <SortableStackSurface
                key={srf.surfaceId}
                surfaceId={srf.surfaceId}
                renderHandle={showStackDragHandles}
                fill={isSingleSurface}
              >
                {(handle) => (
                  <AnimatedListItem skipEntrance={skipEntrance} fill={isSingleSurface}>
                    <AuiSurface
                      surface={srf}
                      onAction={onAction ? (a) => onAction(a, srf.surfaceName ?? '') : undefined}
                      onDismiss={() => handleDismiss(srf.canvasId, srf.surfaceId)}
                      dragHandle={handle}
                    />
                  </AnimatedListItem>
                )}
              </SortableStackSurface>
            ))}
          </AnimatePresence>
        </div>
      </SortableContext>
    </DndContext>
  );
}

interface SortableSurfaceTabButtonProps {
  surface: Surface;
  isActive: boolean;
  onClick: () => void;
}

function SortableSurfaceTabButton({ surface, isActive, onClick }: SortableSurfaceTabButtonProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: surface.surfaceId,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <button
      ref={setNodeRef}
      style={style}
      type="button"
      className={`cursor-grab rounded px-2 py-1 text-xs font-medium active:cursor-grabbing ${
        isActive
          ? 'bg-muted text-foreground'
          : 'text-muted-foreground hover:text-foreground'
      }`}
      onClick={onClick}
      {...attributes}
      {...listeners}
    >
      {cleanDisplayText(surface.title) || surface.surfaceId}
    </button>
  );
}

interface SortableStackSurfaceProps {
  surfaceId: string;
  renderHandle: boolean;
  fill?: boolean;
  children: (handle: React.ReactNode) => React.ReactNode;
}

const SortableStackSurface = forwardRef<HTMLDivElement, SortableStackSurfaceProps>(
  function SortableStackSurface({ surfaceId, renderHandle, fill, children }, externalRef) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: surfaceId,
    });

    const setRef = useCallback(
      (node: HTMLDivElement | null) => {
        setNodeRef(node);
        if (typeof externalRef === 'function') externalRef(node);
        else if (externalRef) externalRef.current = node;
      },
      [setNodeRef, externalRef],
    );

    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.6 : 1,
      zIndex: isDragging ? 10 : undefined,
    };

    const handle = renderHandle ? (
      <button
        type="button"
        aria-label="Reorder surface"
        className="rounded p-1 text-muted-foreground opacity-0 hover:bg-muted hover:text-foreground focus-ring group-hover/surface-actions:opacity-100 cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
    ) : null;

    return (
      <div
        ref={setRef}
        style={style}
        className={fill ? 'flex flex-1 min-h-0 flex-col' : undefined}
        data-surface-sortable-id={surfaceId}
      >
        {children(handle)}
      </div>
    );
  },
);
